import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import './AdminLayout.css';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import PartnerKycPage from '../Pages/PartnerKycPage';
import {
  applyAdminTheme,
  loadAdminTheme,
  loadSidebarPinned,
  saveSidebarPinned,
} from './adminUiPrefs';

const PREFS = 'partner';

const PartnerLayout = () => {
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pinned, setPinned] = useState(() => loadSidebarPinned(PREFS));
  const [kycStatus, setKycStatus] = useState('not_submitted');
  const [kycChecked, setKycChecked] = useState(false);
  const layoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('partner_access_token');
    if (!token) {
      navigate('/partner/login');
      return;
    }

    const storedUser = localStorage.getItem('partnerUser');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      if (parsed.role !== 'partner') {
        navigate('/partner/login');
        return;
      }
      setUser({
        ...parsed,
        own_access: Boolean(parsed.own_access),
        chat_access: parsed.chat_access !== false,
        user_chat_access: parsed.user_chat_access === true,
      });
    }
  }, [navigate]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/kyc/my`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        if (cancelled) return;
        setKycStatus(res.data?.partner_kyc_status || 'not_submitted');
        const ownAccess = Boolean(res.data?.own_access);
        const chatAccess = res.data?.chat_access !== false;
        const userChatAccess = res.data?.user_chat_access === true;
        setUser((prev) => {
          if (!prev || (prev.own_access === ownAccess && prev.chat_access === chatAccess && prev.user_chat_access === userChatAccess)) return prev;
          const next = { ...prev, own_access: ownAccess, chat_access: chatAccess, user_chat_access: userChatAccess };
          try {
            localStorage.setItem('partnerUser', JSON.stringify(next));
          } catch {
            /* ignore */
          }
          return next;
        });
      } catch {
        if (!cancelled) setKycStatus(user.kyc_status || 'not_submitted');
      } finally {
        if (!cancelled) setKycChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    applyAdminTheme(layoutRef.current, loadAdminTheme(PREFS));
    const onTheme = (event) => {
      applyAdminTheme(layoutRef.current, event.detail || loadAdminTheme(PREFS));
    };
    window.addEventListener('pif-admin-theme', onTheme);
    return () => window.removeEventListener('pif-admin-theme', onTheme);
  }, [user]);

  const togglePin = () => {
    const next = !pinned;
    setPinned(next);
    saveSidebarPinned(next, PREFS);
    setSidebarOpen(next);
  };

  if (!user || !kycChecked) return null;
  if (kycStatus !== 'approved') {
    return (
      <div className="admin-layout" style={{ justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <PartnerKycPage embedded onApproved={() => setKycStatus('approved')} />
      </div>
    );
  }

  const chatAccess = user?.chat_access !== false;

  return (
    <div
      ref={layoutRef}
      className={`admin-layout ${pinned ? 'sidebar-pinned' : 'sidebar-unpinned'} ${sidebarOpen ? 'sidebar-drawer-open' : ''}`}
    >
      {!pinned && sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <Sidebar
        userRole="partner"
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        basePath="/partner"
        partnerOwnAccess={Boolean(user?.own_access)}
        partnerChatAccess={chatAccess}
        partnerUserChatAccess={chatAccess && user?.user_chat_access === true}
        prefsScope={PREFS}
        pinned={pinned}
        onTogglePin={togglePin}
      />
      <main className="admin-main">
        <Header
          user={user}
          onMenuToggle={() => setSidebarOpen((open) => !open)}
          basePath="/partner"
          chatAccess={chatAccess}
          prefsScope={PREFS}
        />
        <div className="admin-content">
          <Outlet key={location.pathname} context={{ partnerOwnAccess: Boolean(user?.own_access), partnerChatAccess: chatAccess }} />
        </div>
      </main>
    </div>
  );
};

export default PartnerLayout;
